import type { BufferEditor } from '~publish/legacy/editor/BufferEditor/types.plate'

/**
 * Remove invisible Unicode characters that can be pasted from various platforms
 * These characters include directional marks commonly found in social media content
 *
 * Optimized single regex for performance:
 * - U+202A-202E: Directional formatting characters (LRE, RLE, PDF, LRO, RLO)
 * - U+2066-2069: Directional isolate characters (LRI, RLI, FSI, PDI)
 * - U+200B-200D: Zero width characters (ZWSP, ZWNJ, ZWJ)
 * - U+2060: Word Joiner
 * - U+2063-2064: Invisible separator and plus
 */
const INVISIBLE_CHARS_REGEX =
  /[\u202A-\u202E\u2066-\u2069\u200B-\u200D\u2060\u2063\u2064]/g

const sanitizeText = (text: string): string => {
  return text.replace(INVISIBLE_CHARS_REGEX, '')
}

export const withTextSanitization = (editor: BufferEditor): BufferEditor => {
  const { insertData } = editor

  editor.insertData = (data: DataTransfer) => {
    const text = data.getData('text/plain')

    if (text) {
      const sanitizedText = sanitizeText(text)

      // Only create new DataTransfer if sanitization changed the text
      if (sanitizedText !== text) {
        const sanitizedData = new DataTransfer()

        // Copy all data types but sanitize text/plain
        data.types.forEach((type) => {
          if (type === 'text/plain') {
            sanitizedData.setData(type, sanitizedText)
          } else {
            sanitizedData.setData(type, data.getData(type))
          }
        })

        insertData(sanitizedData)
        return
      }
    }

    insertData(data)
  }

  // Note: We intentionally don't override insertText because:
  // 1. Invisible Unicode characters typically come from paste operations, not typing
  // 2. Sanitizing every keystroke would hurt performance
  // 3. Users rarely type invisible Unicode characters directly

  return editor
}
